import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import L from 'leaflet'
import { Marker, Tooltip } from 'react-leaflet'
import { Compass, Gauge, X } from 'lucide-react'
import { api } from '@pilot-hub/api-client'
import { useModal } from '../../hooks/useModal'
import { himmelsrichtung, textGrad } from './geodaesie'
import '@photo-sphere-viewer/core/index.css'

/*
 * Panorama-Ansicht (Photo Sphere Viewer, als npm-Paket gebündelt – keine CDN-Abhängigkeit zur Laufzeit; der Viewer und three.js werden
 * erst beim ersten Öffnen nachgeladen).
 * Panoramen der Matrice 4TD (DJI Pilot 2, an Bord zusammengesetzt) erkennt der Medien-Worker (typ 'pano', siehe medien-worker/panorama.py):
 *   kugel     360° x 180°: volle Kugel
 *   breit     Teilpanorama im Querformat (180°, Weitwinkel): Bildwinkel aus pano_fov_h/pano_fov_v, begrenzte Ansicht mit Schwenk
 *   vertikal  Teilpanorama im Hochformat
 * Bildfassungen: 'pano-gross' (max. 8192 px), 'pano-klein' (max. 4096 px, sichere WebGL-Textur für iPad/Handy), Rückfall 'verkleinert' (2560 px).
 * Die Bilder werden mit Anmeldung als Blob geladen (img-/Textur-Adressen können keinen Token senden).
 * ANNAHME (ungeprüft mit echter M4TD): Die Bildmitte zeigt in Richtung des Gimbal-Gierwinkels aus dem Foto (nur für die Kompass-Anzeige).
 */

export interface PanoramaInfo {
  id: number
  name: string
  aufnahme_zeit: string | null
  lat: number | null
  lon: number | null
  hoehe_rel_m?: number | null
  gimbal_gier?: number | null
  panorama_art?: 'kugel' | 'breit' | 'vertikal' | null
  pano_fov_h?: number | null
  pano_fov_v?: number | null
  hat_pano_gross?: boolean
  hat_pano_klein?: boolean
  hat_verkleinert?: boolean
  hat_original?: boolean
  dateien_geloescht?: boolean
  einsatz_titel?: string | null
}

export type PanoramaPfad = (id: number, art: 'pano-gross' | 'pano-klein' | 'verkleinert' | 'vorschau' | 'original') => string
/** Standard in Führung und Pilot: /dji/medien/datei/{id}/{art} (api-client setzt /api/v1 davor) */
export const standardPanoramaPfad: PanoramaPfad = (id, art) => `/dji/medien/datei/${id}/${art}`

const ART_TEXT: Record<string, string> = { kugel: 'Kugelpanorama 360°', breit: 'Panorama (Teilbild)', vertikal: 'Vertikalpanorama' }
export const panoramaArtText = (a?: string | null) => (a ? ART_TEXT[a] ?? 'Panorama' : 'Panorama')

const zeitText = (z: string | null | undefined) => z ? new Date(z).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'

/** Mobilgerät oder schwache GPU: 4096-px-Fassung (viele iPads/Handys erlauben keine größere WebGL-Textur) */
function maxTextur(): number {
  try {
    const c = document.createElement('canvas')
    const gl = (c.getContext('webgl2') || c.getContext('webgl')) as WebGLRenderingContext | null
    return gl ? (gl.getParameter(gl.MAX_TEXTURE_SIZE) as number) : 0
  } catch { return 0 }
}
function startQualitaet(): 'gross' | 'klein' {
  const grob = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
  const klein = Math.min(screen.width, screen.height) < 900
  return grob || klein || maxTextur() < 8192 ? 'klein' : 'gross'
}

export function PanoramaDialog({ pano, pfad = standardPanoramaPfad, onClose }: { pano: PanoramaInfo; pfad?: PanoramaPfad; onClose: () => void }) {
  const wurzel = useRef<HTMLDivElement>(null)
  const behaelter = useRef<HTMLDivElement>(null)
  const [qual, setQual] = useState<'gross' | 'klein'>(startQualitaet)
  const [status, setStatus] = useState<'laden' | 'bereit' | 'fehler'>('laden')
  const [fehler, setFehler] = useState('')
  const [blick, setBlick] = useState<number | null>(null)
  useModal(true, wurzel, onClose, 'button[data-schliessen]')

  const hatGross = !!pano.hat_pano_gross, hatKlein = !!pano.hat_pano_klein
  // gewünschte Fassung, sonst die nächstbeste
  const art: 'pano-gross' | 'pano-klein' | 'verkleinert' =
    qual === 'gross' && hatGross ? 'pano-gross' : hatKlein ? 'pano-klein' : hatGross ? 'pano-gross' : 'verkleinert'

  useEffect(() => {
    let lebt = true
    let viewer: { destroy: () => void } | null = null
    let url: string | null = null
    setStatus('laden'); setFehler(''); setBlick(null)
    ;(async () => {
      try {
        const [res, psv] = await Promise.all([
          api.get(pfad(pano.id, art), { responseType: 'blob' }),
          import('@photo-sphere-viewer/core'),
        ])
        if (!lebt) return
        url = URL.createObjectURL(res.data as Blob)
        const dim = await new Promise<{ w: number; h: number }>((ok, nein) => {
          const im = new Image()
          im.onload = () => ok({ w: im.naturalWidth, h: im.naturalHeight })
          im.onerror = () => nein(new Error('Bild nicht lesbar'))
          im.src = url!
        })
        if (!lebt || !behaelter.current) return
        // Teilpanorama: Bild sitzt mittig in der Kugel (gleichmäßige Winkelverteilung, Bildwinkel aus der Datenbank)
        const fh = pano.pano_fov_h ?? 360, fv = pano.pano_fov_v ?? 180
        const voll = pano.panorama_art === 'kugel' || (fh >= 355 && fv >= 175)
        const fullW = voll ? dim.w : Math.round(dim.w * 360 / fh)
        const panoData = voll ? undefined : {
          fullWidth: fullW, fullHeight: Math.round(fullW / 2), croppedWidth: dim.w, croppedHeight: dim.h,
          croppedX: Math.round((fullW - dim.w) / 2), croppedY: Math.round((fullW / 2 - dim.h) / 2),
        }
        // Teilpanorama: größter Bildwinkel = kleinerer der beiden Bildwinkel des Bildes, damit in der Bildmitte kein leerer
        // (schwarzer) Bereich sichtbar wird (konservativ, unabhängig davon, auf welche Seite PSV den Winkel bezieht)
        const maxFov = voll ? undefined : Math.max(25, Math.min(90, fh, fv))
        const v = new psv.Viewer({
          container: behaelter.current, panorama: url, panoData,
          ...(maxFov ? { maxFov, minFov: Math.min(20, maxFov / 2) } : {}),
          navbar: ['zoom', 'move', 'fullscreen'], loadingTxt: 'Panorama wird geladen …',
          defaultZoomLvl: voll ? 30 : 0, mousewheelCtrlKey: false, touchmoveTwoFingers: false, keyboard: 'always',
          lang: { zoom: 'Zoom', zoomOut: 'Verkleinern', zoomIn: 'Vergrößern', moveUp: 'Nach oben', moveDown: 'Nach unten', moveLeft: 'Nach links',
                  moveRight: 'Nach rechts', fullscreen: 'Vollbild', loading: 'Laden …', loadError: 'Panorama konnte nicht geladen werden' },
        })
        viewer = v
        const gier = pano.gimbal_gier
        v.addEventListener('ready', () => { if (lebt) setStatus('bereit') }, { once: true })
        v.addEventListener('panorama-error', () => { if (lebt) { setStatus('fehler'); setFehler('Panorama konnte nicht dargestellt werden (WebGL?)') } })
        v.addEventListener('position-updated', e => {
          if (gier == null || !lebt) return
          setBlick(((gier + (e.position.yaw * 180) / Math.PI) % 360 + 360) % 360)
        })
      } catch (e: any) {
        if (!lebt) return
        setStatus('fehler')
        setFehler(e?.response?.status === 404 ? 'Panorama nicht (mehr) vorhanden (Aufbewahrungsfrist)' : 'Panorama konnte nicht geladen werden')
      }
    })()
    return () => { lebt = false; try { viewer?.destroy() } catch { /* ignorieren */ } if (url) URL.revokeObjectURL(url) }
  }, [pano.id, art, pfad, pano.pano_fov_h, pano.pano_fov_v, pano.panorama_art, pano.gimbal_gier])

  return createPortal(
    <div ref={wurzel} role="dialog" aria-modal="true" aria-label="Panorama" tabIndex={-1}
      className="fixed inset-0 z-[1100] flex flex-col ui-dunkel bg-black text-white">
      <div className="flex shrink-0 items-center gap-2 border-b border-white/15 bg-slate-900/95 px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{panoramaArtText(pano.panorama_art)} · {zeitText(pano.aufnahme_zeit)}</div>
          <div className="truncate text-xs text-white/60">
            {pano.einsatz_titel ? `${pano.einsatz_titel} · ` : ''}{pano.name}
            {pano.hoehe_rel_m != null && <> · Höhe {Math.round(pano.hoehe_rel_m)} m</>}
            {art === 'pano-klein' ? ' · 4096 px' : art === 'pano-gross' ? ' · 8192 px' : ' · 2560 px'}
          </div>
        </div>
        {hatGross && hatKlein && (
          <button type="button" onClick={() => setQual(q => (q === 'gross' ? 'klein' : 'gross'))}
            className="flex min-h-[44px] items-center gap-1.5 rounded-lg border border-white/25 px-3 text-xs font-semibold hover:bg-white/10"
            title="Auflösung wechseln (4096 px = schnell und sicher auf iPad/Handy)">
            <Gauge className="h-4 w-4" />{qual === 'gross' ? 'Hoch (8192)' : 'Schnell (4096)'}
          </button>
        )}
        <button type="button" data-schliessen onClick={onClose} aria-label="Schließen"
          className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/25 hover:bg-white/10"><X className="h-5 w-5" /></button>
      </div>
      <div className="relative min-h-0 flex-1">
        <div ref={behaelter} className="absolute inset-0" />
        {status === 'laden' && <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-white/70">Panorama wird geladen …</div>}
        {status === 'fehler' && <div role="alert" className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-red-300">{fehler}</div>}
        {status === 'bereit' && blick != null && (
          <div className="pointer-events-none absolute bottom-16 left-3 flex items-center gap-2 rounded-xl bg-slate-900/80 px-3 py-2 text-xs" aria-live="off">
            <Compass className="h-5 w-5 text-cyan-300" style={{ transform: `rotate(${-blick}deg)` }} />
            <span>Blick {textGrad(blick)} {himmelsrichtung(blick)}</span>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

// --- Kartensymbol ---------------------------------------------------------------------------------------------------
const PANO_SVG = `<svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true"><circle cx="14" cy="14" r="12" fill="#7c3aed" stroke="#fff" stroke-width="2"/>`
  + `<ellipse cx="14" cy="14" rx="8" ry="3.4" fill="none" stroke="#fff" stroke-width="1.6"/><ellipse cx="14" cy="14" rx="3.4" ry="8" fill="none" stroke="#fff" stroke-width="1.2" opacity=".7"/>`
  + `<circle cx="14" cy="14" r="1.8" fill="#fff"/></svg>`
const panoIcon = L.divIcon({ html: `<div style="filter:drop-shadow(0 1px 3px rgba(0,0,0,.6))">${PANO_SVG}</div>`, className: '', iconSize: [28, 28], iconAnchor: [14, 14] })

/** Innerhalb von <MapContainer>: Panoramasymbole am Aufnahmeort, Klick öffnet den Viewer. */
export function PanoramaLayer({ panoramen, pfad = standardPanoramaPfad }: { panoramen: PanoramaInfo[]; pfad?: PanoramaPfad }) {
  const [offen, setOffen] = useState<PanoramaInfo | null>(null)
  return (
    <>
      {panoramen.filter(p => p.lat != null && p.lon != null).map(p => (
        <Marker key={p.id} position={[p.lat!, p.lon!]} icon={panoIcon} zIndexOffset={200}
          eventHandlers={{ click: () => setOffen(p) }}>
          <Tooltip direction="top" offset={[0, -14]}>{panoramaArtText(p.panorama_art)} · {zeitText(p.aufnahme_zeit)} (Klick öffnet)</Tooltip>
        </Marker>
      ))}
      {offen && <PanoramaDialog pano={offen} pfad={pfad} onClose={() => setOffen(null)} />}
    </>
  )
}
